import { progressAtom, serverLoadingAtom } from "@/hooks/headerHooks";
import { useSetAtom } from "jotai";
import { useCallback } from "react";
import { i18n } from "../../../../../i18n-config";

/**
 * Computes whether the navigation is a route change (new page) or a params-only
 * change (same page, different search params).  Pure function — no hooks.
 */
function computeRouterChange(
  href: string,
  currentPath: string,
  currentSearch: string,
) {
  if (href.startsWith("http"))
    return { startProgress: false, loading: false };

  // Strip locale prefixes for comparison
  const stripLang = (s: string) => {
    let p = s;
    for (const locale of i18n.locales) {
      const prefix = `/${locale}`;
      if (p.startsWith(prefix + "/") || p === prefix) {
        p = p.slice(prefix.length) || "/";
        break;
      }
    }
    return p.replaceAll("/", "");
  };

  const url = stripLang(currentPath);
  const hrefStr = stripLang(href);
  const qIdx = hrefStr.indexOf("?");
  const hrefPath = qIdx < 0 ? hrefStr : hrefStr.substring(0, qIdx);
  const hrefParams = qIdx < 0 ? "" : href.substring(href.indexOf("?") + 1);

  return {
    startProgress: hrefPath !== url,
    loading: hrefPath === url && hrefParams !== currentSearch,
  };
}

/**
 * Lightweight hook that provides a `setRouterChange(href)` callback.
 *
 * **Key optimisation**: we no longer call `usePathname()` or `useSearchParams()`
 * during render.  Instead we read `window.location` lazily inside the click
 * handler.  This eliminates ~30-50 `useSearchParams()` subscriptions on a
 * typical product-grid page — each of which forced Next.js to wrap the Link
 * in a Suspense boundary and re-render on every URL change.
 */
export const useRouterChange = () => {
  const setProgress = useSetAtom(progressAtom);
  const setServerLoading = useSetAtom(serverLoadingAtom);

  const setRouterChange = useCallback(
    (href: string) => {
      // Read current URL lazily at click time — avoids subscribing to
      // usePathname / useSearchParams during render of every Link.
      const currentPath = window.location.pathname;
      const currentSearch = window.location.search.replace(/^\?/, "");
      const { startProgress, loading } = computeRouterChange(
        href,
        currentPath,
        currentSearch,
      );
      setProgress(startProgress);
      setServerLoading(loading);
    },
    [setProgress, setServerLoading],
  );

  return { setRouterChange };
};
